<script setup lang="ts">
import { NuxtLink } from '#components';

const props = withDefaults(
    defineProps<{
        type?: TCompTypes,
        size?: TCompSizes,
        variant?: TVariants,
        prependIcon?: TIcon,
        appendIcon?: TIcon,
        pill?: boolean,
        loading?: boolean,
        disabled?: boolean,
        link?: boolean,
    }>(),
    {
        type: 'Primary',
        variant: 'primary',
        size: 'Medium',
    }
);
</script>

<template>
    <VariantColors :variant="props.variant">
        <template #default="{ variantColors }">
            <component
                :is="props.link ? NuxtLink : 'button'"
                v-bind="$attrs"
                class="relative flex-center"
                :class="[
                    $style['base-btn'],
                    variantColors,
                    $style[props.type],
                    $style[props.size],
                    props.pill ? 'rounded-3xl' : 'rounded-lg'
                ]"
                :disabled="props.disabled || props.loading"
            >
                <!-- *** .btn-slot-parent: applied at the component usage level to style this div element *** -->
                <div 
                    class="btn-slot-parent flex-center gap-3"
                    :class="props.loading && 'opacity-0'"
                >
                    <Icon
                        v-if="props.prependIcon"
                        :name="props.prependIcon.name"
                        :class="props.prependIcon?.class"
                    />

                    <slot />

                    <Icon
                        v-if="props.appendIcon"
                        :name="props.appendIcon.name"
                        :class="props.appendIcon?.class"
                    />
                </div>
                <Icon 
                    v-if="props.loading" 
                    name="svg-spinners:6-dots-rotate" 
                    class="absolute" 
                />
            </component>
        </template>
    </VariantColors>
</template>

<style module>
.base-btn {
    color: var(--btn-text);
    background: var(--btn-bg);
    cursor: pointer;
    user-select: none;
}
.base-btn:disabled {
    opacity: 60% !important;
    cursor: default !important;
    pointer-events: none !important;
}


/* Types */
.Primary {
    --btn-bg: linear-gradient(to right, var(--c-600), var(--c-700));
    --btn-text: var(--c-50);
    box-shadow: var(--shadow-m);
}
.Primary:hover {
    --btn-bg: linear-gradient(to right, var(--c-700), var(--c-700));
    box-shadow: none;
}
.Primary:focus {
    --btn-bg: linear-gradient(to right, var(--c-500), var(--c-500));
    box-shadow: 0 0 0 2px var(--c-200);
}
.Primary:active {
    --btn-bg: linear-gradient(to right, var(--c-500), var(--c-500));
    box-shadow: 
        0 0 0 2px var(--c-50),
        0 0 0 4px var(--c-400),
        var(--shadow-m);
}
.Primary:disabled {
    box-shadow: none;
}

.Secondary {
    --btn-bg: var(--c-100);
    --btn-text: var(--c-600);
    box-shadow: 0 0 0 1px var(--c-600);
}
.Secondary:hover {
    --btn-bg: var(--c-200);
    --btn-text: var(--c-700);
    box-shadow: 0 0 0 1px var(--c-400);
}
.Secondary:focus {
    --btn-bg: var(--c-100);
    --btn-text: var(--c-600);
    box-shadow: 0 0 0 2px var(--c-200);
}
.Secondary:active {
    --btn-bg: var(--c-100);
    box-shadow: 
        0 0 0 2px var(--c-50),
        0 0 0 4px var(--c-100);
}

.Outline {
    --btn-bg: transparent;
    --btn-text: var(--c-600);
    box-shadow: 0 0 0 1px var(--c-600);
}
.Outline:hover {
    --btn-text: var(--c-700);
    box-shadow: 0 0 0 1px var(--c-700);
}
.Outline:focus {
    --btn-text: var(--c-600);
    box-shadow: 0 0 0 2px var(--c-100);
}
.Outline:active {
    --btn-text: var(--c-600);
    box-shadow: 
        0 0 0 2px var(--c-100),
        0 0 0 4px var(--c-200);
}

.Thirdly {
    --btn-bg: transparent;
    --btn-text: var(--c-600);
}
.Thirdly:hover {
    --btn-text: var(--c-700);
}
.Thirdly:focus {
    --btn-text: var(--c-400);
}
.Thirdly:active {
    --btn-text: var(--c-500);
}


/* Sizes */
.Small {
    height: 36px;
    padding: 8px 16px;
    composes: text-btn-s from global;
}
.Small :global(.iconify) {
    width: 20px;
    height: 20px;
}

.Medium {
    height: 40px;
    padding: 8px 16px;
    composes: text-btn-m from global;
}
.Medium :global(.iconify) {
    width: 20px;
    height: 20px;
}

.Large {
    height: 44px;
    padding: 10px 16px;
    composes: text-btn-l from global;
}
.Large :global(.iconify) {
    width: 24px;
    height: 24px;
}
</style>